<template>
  <form @submit.prevent>
    <div class="form nft-order">
      <NFTCurrencySelector
        v-if="fiatInputs"
        key="buyFiat"
        name="fiat"
        type="text"
        :label="$t('NFTCurrencySelector.label.spend')"
        :placeholder="$t('NFTCurrencySelector.placeholder.spend')"
        :defaultValue="{
          fiatAmount: form.fiatAmount,
          fiatType: form.fiatType,
        }"
        :items="fiatInputs"
        :isLoading="isFetchingData.fiatType"
        :errors="store.getErrorByField('fiatAmount')"
        @inputValueUpdate="updateCurrencyInput"
        @dropdownValueUpdate="updateDropdown"
      />

      <div class="nft-order__headline">{{ $t("NFTBuyForm.label.nft") }}</div>

      <!-- todo  he-452 to add loading state prop-->
      <WalletAddressField
        v-if="
          store.form.walletAddress.fromApi === false ||
          globalStore.getMerchantSettings?.readOnlyWalletAddress === true
        "
        :name="'walletAddress'"
        v-model:walletAddress="form.walletAddress"
        :errors="store.getErrorByField('walletAddress')"
      />

      <PaymentInput
        key="buyPayments"
        :items="orderStore.filterPaymentsByFiatAndCoin"
        @paymentSelected="updateDropdown"
        label="Pay with"
        :hideDescription="true"
      />

      <ButtonBase
        :isDisabled="isButtonDisabled"
        :isLoading="globalStore.getIsFormLoading"
        @onButtonClick="handleFormSubmit"
      >
        {{ $t("ButtonBase.label.createOrder") }}
      </ButtonBase>
    </div>
  </form>
</template>

<script setup>
// ====================================
// Packages
// ====================================
import { ref, watch, computed, provide } from "vue";
import { useRoute, useRouter } from "vue-router";
import { whenever } from "@vueuse/core";
// ====================================
// Storage
// ====================================
import { useGlobalStore } from "@/stores/globalStore";
import { useNFTOrderFormStore } from "@/stores/forms/nftOrderFormStore";
import { useOrderFormStore } from "@/stores/forms/orderFormStore";
import { useIdentityQueueStore } from "@/stores/identityQueueStore";
import { useNFTDataStore } from "@/stores/forms/nftDataStore";

// ====================================
// Composables
// ====================================
import useApiHelpers from "@/composables/api/helpers";
import useDynamicPricingCalculator from "@/composables/helpers/useDynamicPricingCalculator";
import useOrderEndpoints from "@/composables/api/useOrderEndpoints";
import useWatchAndUpdate from "@/composables/helpers/useWatchAndUpdate";
import useWalletAddressEventValidator from "@/composables/watchers/useWalletAddressEventValidator";
import useTransactionalLimits from "@/composables/watchers/useTransactionalLimits";
import useWalletTagByBlockchain from "@/composables/watchers/useWalletTagByBlockchain";
import useOrderCreation from "@/composables/api/useOrderCreation";
import useOrderForm from "@/composables/shared/useOrderForm";
// ====================================
// Components
// ====================================
import BaseInput from "@/components/ui/inputs/BaseInput.vue";
import ButtonBase from "@/components/ui/buttons/ButtonBase.vue";
import NFTCurrencySelector from "@/components/ui/inputs/NFTCurrencySelector.vue";
import FormSelect from "@/components/ui/selects/FormSelect.vue";
import PaymentInput from "@/components/ui/inputs/PaymentInput.vue";
import TwoColumnTable from "@/components/ui/tables/TwoColumnTable.vue";
import WalletAddressField from "@/components/ui/inputs/WalletAddressField.vue";
import FormInputField from "@/components/ui/inputs/FormInputField.vue";

const route = useRoute();
const router = useRouter();
const { postRequest } = useApiHelpers();
const { getFiats, getCoins, initOrder } = useOrderEndpoints();
const { watchFormAndUpdateStore } = useWatchAndUpdate();
const { watchWalletAddressAndHandleEvents } = useWalletAddressEventValidator();
const { watchAndHandleFiatValidation, watchAndHandleCoinValidation } =
  useTransactionalLimits();
const { watchWalletTagFieldByBlockchain } = useWalletTagByBlockchain();
const { dynamicPricingCalculator } = useDynamicPricingCalculator();
const { setFormRefsAndUpdateStore } = useOrderCreation();
const { findBlockchainsByCoin } = useOrderForm();
const orderStore = useOrderFormStore();
const store = useNFTOrderFormStore();
const globalStore = useGlobalStore();
const nftDataStore = useNFTDataStore();
const identityQueueStore = useIdentityQueueStore();

const props = defineProps({
  initialData: {
    type: Object,
    default: () => {},
  },
});
const fiatResponse = ref([]);
const fiatInputs = ref([]);
const coinResponse = ref([]);
const coinInputs = ref([]);
const blockchainsByCoin = ref([]);
const defaultBlockchain = ref("");
const form = ref({});
const provideBuyUiDisable = ref({});
const isFetchingData = ref({
  fiatAmount: true,
});
const setDefaultsFromUrl = ref(false);

const isButtonDisabled = computed(() => store.formHasErrors
  || globalStore.getIsFormLoading
  || store.feeData.isLoading
  || orderStore.filterPaymentsByFiatAndCoin?.length === 0);

const findAllBlockchainCodesByCoins = computed(() =>
  coinResponse.value?.data?.data?.coins
    .find((el) => el.coin_code === form.value.coinType)
    .blockchains.map((el) => el.code)
);

const walletTagByBlockchainData = computed(
  () => store.getWalletMemoByBlockchain
);

const disabledBlockchainSelect = computed(() => {
  if (
    blockchainsByCoin.value &&
    blockchainsByCoin.value.includes("No valid blockchains available")
  ) {
    return true;
  }

  return false;
});

const handleFormSuccess = (res) => {
  if (res.data.rel.route === "account") {
    router.push("/verification");
  }
  if (res.data.rel.route === "authentication") {
    router.push("/verification/code");
  }
  if (res.data.rel.route === "prepaid" || res.data.rel.route === "postpaid") {
    identityQueueStore.fetchNextIdentityRoute(route).then((nextRoute) => {
      router.push(nextRoute);
    });
  }
};

const handleFormSubmit = async () => {
  globalStore.setIsFormLoading(true);
  // don't refresh session if from API
  if (orderStore.isOrderFromMerchantApi) {
    const { lockFiat, ...rest } = store.getFormPayload;
    postRequest(
      "/orders/nft/buy",
      {
        orderId: orderStore.getMerchantApiData?.orderId,
        lockFiat: orderStore.getMerchantApiData?.lockFiat,
        ...rest,
      },
      handleFormSuccess
    );
    return;
  }
  await initOrder(); // clears the session
  postRequest(
    "/orders/nft/buy",
    {
      reference: nftDataStore.getNftData?.nftId,
      ...store.getFormPayload,
    },
    handleFormSuccess
  );
};

const updateCurrencyInput = (inputData) => {
  const field = Object.keys(inputData)[0];
  const value = Object.values(inputData)[0];
  const lockFiat = field === "fiatAmount";

  form.value[field] = value;
  form.value.lockFiat = lockFiat;

  dynamicPricingCalculator(field, value, form, store);
};

const updateDropdown = (dropdownData) => {
  const field = Object.keys(dropdownData)[0];
  const value = Object.values(dropdownData)[0];
  if (value) form.value[field] = value;
  dynamicPricingCalculator(field, value, form, store);
};

const changeLoadingValueByName = (name, isLoading) => {
  isFetchingData.value[name] = isLoading;
};

const runOnMounted = async () => {
  fiatResponse.value = await getFiats("nft-buy");
  coinResponse.value = await getCoins("nft-buy");

  fiatInputs.value = fiatResponse.value.data.data.fiats.map((el) => {
    return el.fiat_code;
  });
  coinInputs.value = coinResponse.value.data.data.coins.map(
    (el) => el.coin_code
  );

  // todo change to order store actiopn
  store.setCoinData(coinResponse.value.data.data.coins);

  // swift the stop loading here to improve the UX
  changeLoadingValueByName("fiatType", false);
};

provide("isDisabledByKey", provideBuyUiDisable);

watch(
  () => form.value.coinType,
  () => {
    store.setCoinType(form.value.coinType);
    findBlockchainsByCoin(store, form, defaultBlockchain, blockchainsByCoin);
    // In case of NFT referral order, it shall always fulfil to the default blockchain
    if (!orderStore.isOrderFromMerchantApi) {
      form.value.blockchain = defaultBlockchain.value;
    }
  },
  { deep: true }
);

watch(
  () => form.value.blockchain,
  (selectedBlockchain) => {
    store.triggerWalletValidation(form.value.walletAddress, selectedBlockchain);
  },
  { deep: true }
);

watch(
  () => globalStore.$state.token,
  (val) => {
    if (val) {
      runOnMounted();
    }
  },
  { deep: true, immediate: true }
);

// if the order is created with fiat amount, wait for the first calculation and change the lock fiat to false and enable the UI so that customers can change currency
watch(
  () => store.form.coinAmount.value && store.form.lockFiat.value,
  () => {
    if (
      !isNaN(parseFloat(store.form.coinAmount.value)) &&
      store.form.lockFiat.value
    ) {
      provideBuyUiDisable.value.fiatType = false;
      store.setLockCoin();
    }
  },
  { deep: true, immediate: true }
);

// when coin and fiat api calls are done + props are set from OrderView
// call merchApi
whenever(
  () =>
    fiatInputs.value.length > 0 &&
    typeof props.initialData.areDefaultsFromUrl === "boolean",
  () => {
    setFormRefsAndUpdateStore(
      props.initialData.urlData,
      form,
      store,
      fiatInputs.value,
      coinInputs.value
    );
    if (
      props.initialData.disableUIKeys &&
      props.initialData.orderMode === "NFT-BUY"
    ) {
      provideBuyUiDisable.value = props.initialData.disableUIKeys;
    }
  },
  { deep: true }
);

watchFormAndUpdateStore(form, store, () => {});
watchWalletAddressAndHandleEvents(
  form,
  store,
  findAllBlockchainCodesByCoins,
  (filteredBlockchains) => {
    // if the selected coin also has the selected blockchain, don't update the value
    const selectedBlockchain = form.value.blockchain;
    const selectedBlockchainIsAPartOfValidatedBlockchains =
      filteredBlockchains.find(
        (blockchain) => blockchain.value === selectedBlockchain
      );

    if (selectedBlockchainIsAPartOfValidatedBlockchains) {
      defaultBlockchain.value = selectedBlockchain;
    } else {
      defaultBlockchain.value = "";
    }
    blockchainsByCoin.value = filteredBlockchains;
  }
);
watchAndHandleFiatValidation(form, store);
watchWalletTagFieldByBlockchain(walletTagByBlockchainData, store);
</script>

<style lang="scss">
.nft-order {
  &__headline {
    font-weight: 700;
    font-size: 2.4rem;
    margin-bottom: 2.4rem;
  }

  .payment-select {
    margin-bottom: 2.4rem;

    .dropdown-select {
      border-radius: 10px;
    }
  }

  .button {
    margin-top: 2.4rem;

    @include breakpoint($max-m) {
      margin-top: 5rem;
    }
  }
}
</style>
